/**
 * SearchableSelect — filterable combobox for large lists.
 *
 * Replaces native <select> when the option set can be large (hundreds+).
 * Filters client-side; no server round-trip required up to ~5k options.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  className = '',
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const positionDropdown = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownH = Math.min(260, options.length * 36 + 60);
    const openUpward = spaceBelow < dropdownH && rect.top > dropdownH;
    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      minWidth: 280,
      zIndex: 9999,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [options.length]);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setQuery('');
    positionDropdown();
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [disabled, positionDropdown]);

  const select = useCallback(
    (v: string) => {
      onChange(v);
      setOpen(false);
      setQuery('');
    },
    [onChange],
  );

  const clear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
      setOpen(false);
      setQuery('');
    },
    [onChange],
  );

  // Reposition on scroll / resize
  useEffect(() => {
    if (!open) return;
    const handler = () => positionDropdown();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, positionDropdown]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(t) &&
        dropdownRef.current && !dropdownRef.current.contains(t)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const dropdown = open ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-xl overflow-hidden"
    >
      {/* Search input */}
      <div className="p-2 border-b border-[var(--rule)]">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); setQuery(''); }
            if (e.key === 'Enter' && filtered.length === 1) select(filtered[0].value);
          }}
          placeholder="Type to filter…"
          className="w-full text-sm border-0 outline-none placeholder-gray-400"
        />
      </div>

      {/* Options list */}
      <div className="max-h-56 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-sm text-[var(--text-ter)] text-center">No matches</div>
        ) : (
          filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => select(o.value)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--slate-dim)] transition-colors truncate ${
                o.value === value ? 'bg-[var(--slate-dim)] font-medium text-[var(--text-sec)]' : 'text-[var(--text-pri)]'
              }`}
            >
              {o.label}
            </button>
          ))
        )}
      </div>

      {/* Count footer */}
      {options.length > 10 && (
        <div className="px-3 py-1.5 border-t border-[var(--rule)] text-[11px] text-[var(--text-ter)]">
          {filtered.length} of {options.length}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={`relative ${className}`}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={openDropdown}
        disabled={disabled}
        className="w-full flex items-center gap-1 text-sm border border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] text-left disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)] hover:border-[var(--rule)] transition-colors"
      >
        <span className={`flex-1 truncate ${value ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
          {value ? selectedLabel : placeholder}
        </span>
        {value ? (
          <X
            className="h-3.5 w-3.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] flex-shrink-0"
            onClick={clear}
          />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        )}
      </button>

      {createPortal(dropdown, document.body)}
    </div>
  );
}
